import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';

import { DISMISSED_STORAGE_KEY } from '../../common/constants';

/**
 * Whether the modal has already been shown/dismissed in this browser. Reads are
 * defensive: storage may be unavailable (incognito, disabled cookies) in which
 * case we treat the modal as not-yet-dismissed.
 *
 * Goes through Browser Storage Controls (`AtlBrowserStorageLocal`) rather than
 * raw `window.localStorage`. On real product instances BSC replaces the global
 * `window.localStorage` with a consent-enforcing wrapper, so a direct write to
 * an uncategorized key is silently dropped — which previously caused the modal
 * to reappear after dismissal.
 */
export function hasBeenDismissed(): boolean {
	try {
		return AtlBrowserStorageLocal.getItem(DISMISSED_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
}

/**
 * Persist that the modal has been shown so it is not shown again.
 *
 * Goes through the BSC `setItem` path. The dismissed flag is categorised as
 * strictly necessary storage — it records a "do not show again" UX decision
 * that must persist regardless of the user's optional-storage consent, so the
 * write is not gated behind Functional consent. BSC resolves the key's category
 * from its CDN config and, because it is strictly necessary, always persists the
 * write.
 *
 * `setItem` is async and may reject, so we `await` it inside the try/catch —
 * otherwise a rejected promise would float past this handler unhandled.
 * Persistence is best-effort: a failed write simply means the modal may
 * reappear, so failures are swallowed.
 */
export async function markDismissed(): Promise<void> {
	try {
		await AtlBrowserStorageLocal.setStrictlyNecessaryItem(DISMISSED_STORAGE_KEY, 'true');
	} catch {
		/* Storage unavailable or write rejected — silently no-op. */
	}
}
